import { memo, useEffect, useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import { Copy, Link2Off, Power, PowerOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import {
  useAssignSims,
  useImeiModel,
  useInstanceSims,
  usePendingSimIds,
  useRefreshSims,
  useSetSimProfile,
  useSimCard,
  useSimDevices,
  useSimProfiles,
  useSimUsageForSims,
  useUnassignSims,
  useUpdateSimNote,
} from "@/hooks/useSims";
import {
  PROVIDER_CAN_TOGGLE,
  PROVIDER_INFO,
  PROVIDER_LABELS,
  countryName,
  currentMonthUsageMb,
  formatDataMb,
  formatPricePerGb,
  type SimCard,
} from "@/lib/sims";
import { ProviderBadge, SimStatusBadge } from "./SimBadges";
import { SimActionDialog } from "./SimActionDialog";
import { SimUsageCharts } from "./SimUsageCharts";
import { SimProfileSelect } from "./SimProfileSelect";

const THIRTY_TWO_DAYS_MS = 32 * 24 * 60 * 60 * 1000;

/** Details of one SIM: status, identifiers, usage and device assignment. */
export const SimDetailDialog = memo(function SimDetailDialog({
  simId,
  onOpenChange,
}: {
  simId: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  // Loaded by id so the dialog keeps the SIM when it leaves the list it was
  // opened from (e.g. unassigned from a device panel).
  const { data: sim } = useSimCard(simId);
  const pendingIds = usePendingSimIds();
  const pending = sim ? pendingIds.has(sim.id) : false;
  return (
    <Dialog open={!!simId} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-3xl overflow-y-auto"
        data-testid="sim-detail-dialog"
      >
        {sim && <SimDetail sim={sim} pending={pending} />}
      </DialogContent>
    </Dialog>
  );
});

function SimDetail({ sim, pending }: { sim: SimCard; pending?: boolean }) {
  const refresh = useRefreshSims();
  const [action, setAction] = useState<"activate" | "deactivate" | null>(null);
  const canToggle = PROVIDER_CAN_TOGGLE[sim.provider];
  const [dailySince] = useState(() => Date.now() - THIRTY_TWO_DAYS_MS);
  const simIds = useMemo(() => [sim.id], [sim.id]);
  const { monthly, daily } = useSimUsageForSims(simIds, dailySince);
  const target = useMemo(
    () => ({ kind: "sims" as const, simIds: [sim.id] }),
    [sim.id],
  );

  const copyIccid = () =>
    void navigator.clipboard
      ?.writeText(sim.iccid)
      .then(() => toast.success("ICCID copied"));

  const segments = [sim.simetricSegment1, sim.simetricSegment2]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-base" data-testid="sim-detail-iccid">
            {sim.iccid}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={copyIccid}
            aria-label="Copy ICCID"
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </DialogTitle>
        <DialogDescription asChild>
          <div className="flex flex-wrap items-center gap-2">
            <ProviderBadge provider={sim.provider} />
            <SimStatusBadge sim={sim} pending={pending} />
            {sim.lastSyncedAt && (
              <span className="text-xs">
                Status from{" "}
                {formatDistanceToNow(new Date(sim.lastSyncedAt), {
                  addSuffix: true,
                })}
              </span>
            )}
          </div>
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={!canToggle}
          title={
            canToggle
              ? undefined
              : `${PROVIDER_LABELS[sim.provider]} SIMs cannot be switched here`
          }
          onClick={() => setAction("activate")}
          data-testid="sim-detail-activate"
        >
          <Power className="mr-1.5 h-4 w-4" /> Activate
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!canToggle}
          title={
            canToggle
              ? undefined
              : `${PROVIDER_LABELS[sim.provider]} SIMs cannot be switched here`
          }
          onClick={() => setAction("deactivate")}
          data-testid="sim-detail-deactivate"
        >
          <PowerOff className="mr-1.5 h-4 w-4" /> Deactivate
        </Button>
        {sim.source === "simetric" && (
          <Button
            size="sm"
            variant="ghost"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate({ simIds: [sim.id] })}
          >
            <RefreshCw
              className={`mr-1.5 h-4 w-4 ${refresh.isPending ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        )}
      </div>

      <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1.5 text-sm sm:grid-cols-[max-content_1fr_max-content_1fr]">
        <Detail label="Phone number (MSISDN)" value={sim.msisdn} mono />
        <Detail label="IMSI" value={sim.imsi} mono />
        <Detail label="Rate plan" value={sim.ratePlan} />
        <Detail label="Label" value={sim.label} />
        <Detail label="Simetric label" value={segments || null} />
        <Detail
          label="This month"
          value={
            sim.source === "manual"
              ? null
              : formatDataMb(currentMonthUsageMb(sim))
          }
        />
        <ProviderInfoDetails sim={sim} />
        <ModemDetail imei={sim.imei} />
      </dl>

      <NoteSection sim={sim} />

      <ProfileSection sim={sim} />

      <DeviceAssignment sim={sim} />

      {sim.source === "simetric" && (
        <SimUsageCharts sims={[sim]} daily={daily} monthly={monthly} />
      )}

      <SimActionDialog
        action={action}
        target={action ? target : null}
        onOpenChange={(open) => !open && setAction(null)}
      />
    </>
  );
}

function Detail({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
}) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono text-xs leading-5" : undefined}>
        {value ?? "–"}
      </dd>
    </>
  );
}

/** Static provider facts: APN (copyable) and data price. */
function ProviderInfoDetails({ sim }: { sim: SimCard }) {
  const info = PROVIDER_INFO[sim.provider];
  const copyApn = () =>
    void navigator.clipboard
      ?.writeText(info.apn)
      .then(() => toast.success("APN copied"));
  return (
    <>
      <dt className="text-muted-foreground">APN</dt>
      <dd className="flex items-center gap-1">
        <span
          className="font-mono text-xs leading-5"
          data-testid="sim-detail-apn"
        >
          {info.apn}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5"
          onClick={copyApn}
          aria-label="Copy APN"
        >
          <Copy className="h-3 w-3" />
        </Button>
      </dd>
      <dt className="text-muted-foreground">Price</dt>
      <dd data-testid="sim-detail-price">
        {formatPricePerGb(info.priceRappenPerGb)}
      </dd>
    </>
  );
}

/** Modem (IMEI) the network last saw the SIM in, with its make and model. */
function ModemDetail({ imei: rawImei }: { imei: string | null }) {
  const { imei, model, isLoading } = useImeiModel(rawImei);
  return (
    <>
      <dt className="text-muted-foreground">Modem</dt>
      <dd className="sm:col-span-3" data-testid="sim-detail-modem">
        {imei ? (
          <>
            <span className="font-medium">
              {model ? model.name : isLoading ? "…" : "Unknown model"}
            </span>
            <span className="ml-2 font-mono text-xs">IMEI {imei}</span>
            <span className="ml-2 text-xs text-muted-foreground">
              last seen by the network
            </span>
          </>
        ) : (
          <span className="text-muted-foreground">
            {rawImei ? `Not a valid IMEI (${rawImei})` : "Not reported"}
          </span>
        )}
      </dd>
    </>
  );
}

const NOTE_MAX_LENGTH = 2000;

/** Internal free-text note, stored in the hub only (never sent to Simetric). */
function NoteSection({ sim }: { sim: SimCard }) {
  const update = useUpdateSimNote();
  const [draft, setDraft] = useState(sim.note ?? "");

  // Follow external changes (e.g. edited in another tab).
  useEffect(() => setDraft(sim.note ?? ""), [sim.note]);

  const next = draft.trim() || null;
  const changed = next !== (sim.note ?? null);
  const tooLong = draft.trim().length > NOTE_MAX_LENGTH;

  const save = () => {
    if (!changed || tooLong || update.isPending) return;
    update.mutate({ id: sim.id, note: next });
  };

  return (
    <section className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label htmlFor="sim-detail-note" className="text-sm font-medium">
          Note
        </Label>
        <span className="text-xs text-muted-foreground">
          Internal, only stored here. Not sent to{" "}
          {sim.source === "manual" ? "the provider" : "Simetric"}.
        </span>
      </div>
      <Textarea
        id="sim-detail-note"
        data-testid="sim-detail-note"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            save();
          }
        }}
        placeholder="e.g. where the SIM is, who has it, PIN location…"
        rows={3}
        maxLength={NOTE_MAX_LENGTH + 200}
      />
      {tooLong && (
        <p className="text-xs text-destructive">
          Note must be at most {NOTE_MAX_LENGTH} characters.
        </p>
      )}
      <div className="flex justify-end gap-2">
        {changed && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDraft(sim.note ?? "")}
            disabled={update.isPending}
          >
            Discard
          </Button>
        )}
        <Button
          size="sm"
          data-testid="sim-detail-note-save"
          onClick={save}
          disabled={!changed || tooLong || update.isPending}
        >
          {update.isPending ? "Saving…" : "Save note"}
        </Button>
      </div>
    </section>
  );
}

/** The SIM's profile and its preferred network per country. */
function ProfileSection({ sim }: { sim: SimCard }) {
  const { data: profiles } = useSimProfiles();
  const setProfile = useSetSimProfile();
  const profile = profiles.find((p) => p.id === sim.profileID) ?? null;
  const fromRatePlan = sim.profileVia === "rate_plan";

  return (
    <section
      className="space-y-3 rounded-md border p-3"
      data-testid="sim-detail-profile"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Profile</h3>
        <span className="text-sm" data-testid="sim-detail-profile-name">
          {profile ? (
            <>
              {profile.name}
              {profile.isOpen && (
                <span className="text-muted-foreground"> · open</span>
              )}
              <span className="text-muted-foreground">
                {fromRatePlan
                  ? ` · from rate plan ${sim.ratePlan}`
                  : " · set by hand"}
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">No profile</span>
          )}
        </span>
      </div>
      {profile && profile.networks.length > 0 && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
          {profile.networks.map((n) => (
            <div key={n.countryCode} className="contents">
              <dt className="text-muted-foreground">
                {countryName(n.countryCode)}
              </dt>
              <dd>
                {n.networks.length > 0 ? n.networks.join(" › ") : "–"}
                {n.note && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    {n.note}
                  </span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {profile?.isOpen && profile.networks.length === 0 && (
        <p className="text-sm text-muted-foreground">Uses any network.</p>
      )}
      {fromRatePlan ? (
        <p className="text-xs text-muted-foreground">
          The carrier rate plan selects this profile. Change the rate plan to
          change the profile.
        </p>
      ) : (
        <div className="max-w-xs space-y-1.5">
          <Label>Set profile</Label>
          <SimProfileSelect
            profiles={profiles}
            providers={[sim.provider]}
            value={sim.manualProfileID}
            disabled={setProfile.isPending}
            onChange={(profileId) =>
              setProfile.mutate({ simIds: [sim.id], profileId })
            }
            testId="sim-detail-profile-select"
          />
        </div>
      )}
    </section>
  );
}

/** Assign, move or unassign the SIM (device + optional slot). */
function DeviceAssignment({ sim }: { sim: SimCard }) {
  const { data: devices, tagIds, isLoading } = useSimDevices();
  const assign = useAssignSims();
  const unassign = useUnassignSims();
  const [instanceId, setInstanceId] = useState<string | null>(
    sim.equipmentInstanceID,
  );
  const [slot, setSlot] = useState(sim.slot ? String(sim.slot) : "");

  // Follow external changes (e.g. assigned from elsewhere).
  useEffect(() => {
    setInstanceId(sim.equipmentInstanceID);
    setSlot(sim.slot ? String(sim.slot) : "");
  }, [sim.equipmentInstanceID, sim.slot]);

  const { data: deviceSims } = useInstanceSims(instanceId ?? undefined);
  const takenSlots = deviceSims
    .filter((s) => s.id !== sim.id && s.slot !== null)
    .map((s) => s.slot as number)
    .sort((a, b) => a - b);

  const slotNumber = slot.trim() ? Number(slot) : null;
  const slotInvalid =
    slotNumber !== null && (!Number.isInteger(slotNumber) || slotNumber < 1);
  const slotTaken = slotNumber !== null && takenSlots.includes(slotNumber);
  const changed =
    instanceId !== sim.equipmentInstanceID || slotNumber !== sim.slot;

  const submit = () => {
    if (!instanceId) return;
    assign.mutate({
      simIds: [sim.id],
      equipmentInstanceId: instanceId,
      slot: slotNumber,
    });
  };

  return (
    <section className="space-y-3 rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Device</h3>
        <span className="text-sm" data-testid="sim-detail-device">
          {sim.equipmentInstanceID ? (
            <>
              {sim.deviceName}
              {sim.slot ? (
                <span className="text-muted-foreground">
                  {" "}
                  · Slot {sim.slot}
                </span>
              ) : null}
            </>
          ) : (
            <span className="text-muted-foreground">Not assigned</span>
          )}
        </span>
      </div>
      {tagIds.length === 0 && !isLoading ? (
        <p className="text-sm text-muted-foreground">
          No SIM device tags configured yet. Choose which equipment can hold
          SIMs in{" "}
          <Link to="/admin/settings" className="underline">
            Settings
          </Link>
          .
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-56 flex-1 space-y-1.5">
            <Label>
              {sim.equipmentInstanceID ? "Move to device" : "Assign to device"}
            </Label>
            <EntityCombobox
              testId="sim-detail-device-picker"
              options={devices.map((d) => ({ value: d.id, label: d.name }))}
              value={instanceId}
              onChange={setInstanceId}
              placeholder={isLoading ? "Loading devices…" : "Select device…"}
              searchPlaceholder="Search devices…"
              emptyMessage="No matching device"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sim-detail-slot">Slot (optional)</Label>
            <Input
              id="sim-detail-slot"
              data-testid="sim-detail-slot"
              inputMode="numeric"
              value={slot}
              onChange={(e) => setSlot(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && changed && submit()}
              placeholder="e.g. 1"
              className="w-24"
            />
          </div>
          <Button
            data-testid="sim-detail-assign"
            onClick={submit}
            disabled={
              !instanceId ||
              !changed ||
              slotInvalid ||
              slotTaken ||
              assign.isPending
            }
          >
            {assign.isPending
              ? "Saving…"
              : sim.equipmentInstanceID
                ? "Save"
                : "Assign"}
          </Button>
          {sim.equipmentInstanceID && (
            <Button
              variant="outline"
              data-testid="sim-detail-unassign"
              disabled={unassign.isPending}
              onClick={() => unassign.mutate([sim.id])}
            >
              <Link2Off className="mr-1.5 h-4 w-4" /> Unassign
            </Button>
          )}
        </div>
      )}
      {instanceId && (
        <p className="text-xs text-muted-foreground">
          Slots taken on this device:{" "}
          {takenSlots.length > 0 ? takenSlots.join(", ") : "none"}
        </p>
      )}
      {slotInvalid && (
        <p className="text-xs text-destructive">
          Slot must be a positive whole number.
        </p>
      )}
      {slotTaken && (
        <p className="text-xs text-destructive">
          Slot {slotNumber} is already taken on this device.
        </p>
      )}
    </section>
  );
}
